import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Scale } from "lucide-react";
import { useProjectAdjustments, computeAdjustmentTotals } from "@/hooks/useWOAdjustments";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  projectId: string;
  projectName?: string;
}

export const AdjustmentsDashboardCard = ({ projectId, projectName }: Props) => {
  const { data } = useProjectAdjustments(projectId);

  if (!data?.adjustments?.length) return null;

  const { adjustments, workOrders } = data;
  const totals = computeAdjustmentTotals(adjustments);

  // Group by work order
  const byWO: Record<string, { debits: number; credits: number; name: string }> = {};
  for (const wo of workOrders) {
    const woAdjs = adjustments.filter(a => a.work_order_id === wo.id && ["approved", "applied"].includes(a.status));
    if (!woAdjs.length) continue;
    const deb = woAdjs.filter(a => a.direction === "debit").reduce((s, a) => s + Number(a.amount), 0);
    const cre = woAdjs.filter(a => a.direction === "credit").reduce((s, a) => s + Number(a.amount), 0);
    byWO[wo.id] = {
      debits: deb,
      credits: cre,
      name: (wo as any).vendors?.name ?? wo.number,
    };
  }

  const woEntries = Object.values(byWO);
  if (!woEntries.length && totals.pendingCount === 0 && totals.disputedCount === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Scale className="h-4 w-4 text-muted-foreground" /> Adjustments
          {projectName && <span className="text-muted-foreground font-normal">— {projectName}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {woEntries.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead className="text-right">Debits</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {woEntries.map((wo, i) => {
                const net = wo.credits - wo.debits;
                return (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium truncate max-w-[160px]">{wo.name}</TableCell>
                    <TableCell className="text-right text-sm text-destructive font-mono">
                      {wo.debits > 0 ? `-${fmt(wo.debits)}` : "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm text-green-600 font-mono">
                      {wo.credits > 0 ? `+${fmt(wo.credits)}` : "—"}
                    </TableCell>
                    <TableCell className={`text-right text-sm font-mono font-semibold ${net >= 0 ? "text-green-600" : "text-destructive"}`}>
                      {net >= 0 ? "+" : ""}{fmt(net)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="border-t-2">
                <TableCell className="font-semibold text-sm">Total</TableCell>
                <TableCell className="text-right text-sm text-destructive font-mono font-semibold">
                  -{fmt(totals.debits)}
                </TableCell>
                <TableCell className="text-right text-sm text-green-600 font-mono font-semibold">
                  +{fmt(totals.credits)}
                </TableCell>
                <TableCell className={`text-right text-sm font-mono font-semibold ${totals.net >= 0 ? "text-green-600" : "text-destructive"}`}>
                  {totals.net >= 0 ? "+" : ""}{fmt(totals.net)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {(totals.pendingCount > 0 || totals.disputedCount > 0) && (
          <p className="text-xs text-muted-foreground mt-2">
            {totals.pendingCount > 0 && `${totals.pendingCount} pending approval`}
            {totals.pendingCount > 0 && totals.disputedCount > 0 && " · "}
            {totals.disputedCount > 0 && `${totals.disputedCount} disputed`}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
